import { useEffect, useRef, useState, type ReactNode } from "react";
import { useI18n } from "../i18n/index.tsx";
import { initials } from "./editorFormat.ts";
import type { EditorProfile } from "./editorProfile.ts";

/**
 * The account menu behind the profile avatar: Billing, Profile (billing details
 * live inside it), Handbook and Sign out. Opens on click, closes on an outside
 * pointer-down or Escape. A real menu-button (`aria-haspopup`/`aria-expanded`,
 * `role="menu"`/`menuitem`) so it is keyboard- and screen-reader-navigable.
 *
 * `trigger` replaces the plain initials circle - the hub-design dashboard
 * passes its Trust Battery ring (`EditorHub.tsx#AvatarRing`).
 *
 * @module web/screens/EditorProfileMenu
 */
export function ProfileMenu({ me, onBilling, onProfile, onHandbook, signOut, trigger, triggerClassName = "ed-avatar" }: { me: EditorProfile; onBilling?: (() => void) | undefined; onProfile?: (() => void) | undefined; onHandbook?: (() => void) | undefined; signOut: () => void; trigger?: ReactNode; triggerClassName?: string }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    function onPointer(e: PointerEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("pointerdown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const item = (action: (() => void) | undefined) => () => {
    setOpen(false);
    if (action) action();
    else window.location.assign("/editor");
  };

  return (
    <div className="ed-profile" ref={wrapRef}>
      <button
        type="button"
        className={triggerClassName}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t("dashboard.accountMenu")}
        onClick={() => setOpen((v) => !v)}
      >
        {trigger ?? initials(me.displayName)}
      </button>
      {open && (
        <div className="ed-menu" role="menu">
          <button type="button" role="menuitem" className="ed-menu-item" onClick={item(onBilling)}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="5" width="20" height="14" rx="2.5"/><path d="M2 10h20"/></svg>
            {t("dashboard.menuBilling")}
          </button>
          <button type="button" role="menuitem" className="ed-menu-item" onClick={item(onProfile)}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
            {t("dashboard.menuProfile")}
          </button>
          <button type="button" role="menuitem" className="ed-menu-item" onClick={item(onHandbook)}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></svg>
            {t("dashboard.menuHandbook")}
          </button>
          <div className="ed-menu-sep" role="separator" />
          <button
            type="button"
            role="menuitem"
            className="ed-menu-item ed-menu-danger"
            onClick={() => {
              setOpen(false);
              signOut();
            }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
            {t("dashboard.signOut")}
          </button>
        </div>
      )}
    </div>
  );
}

